// Eskom PARCC — application shell.
// Wires the data layer (TanStack Query), authentication (AuthProvider +
// AuthGate) and routing (React Router). The router uses a single layout route
// (the Shell: Header + role-gated Nav + an <Outlet> for the active screen) with
// one child route per screen, all derived from APP_ROUTES. Screens are Phase-1
// placeholders; Phase 2 fills them in place.
//
// Role gating has two layers, both driven by the same APP_ROUTES table so they
// cannot drift:
//   1. Nav only renders links the current role may use (admin: all; reviewer:
//      the three shared screens).
//   2. Admin-only route *elements* are wrapped in <RequireRole role="admin">,
//      so a deep link or stale URL shows the access-denied state rather than the
//      gated screen — navigation filtering alone is never the security boundary.
import { Navigate, createBrowserRouter, RouterProvider } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
import { ThemeProvider } from './lib/theme';
import { UiPrefsProvider } from './lib/uiPrefs';
import { AuthProvider } from './lib/auth';
import { AuthGate } from './features/auth/LoginGate';
import { RequireRoute } from './features/shell/RequireRole';
import { Shell } from './features/shell/Shell';
import { NotFoundPage } from './features/shell/NotFoundPage';
import { RouteError } from './features/shell/RouteError';
import { APP_ROUTES, EXTRA_ROUTES } from './features/shell/routes';

const router = createBrowserRouter([
  {
    path: '/',
    element: <Shell />,
    errorElement: <RouteError />,
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      // One child route per screen, derived from the single route table. Every
      // route is wrapped in <RequireRoute>, which gates on the same
      // canAccessRoute(path, grant) predicate Nav filters on — so a deep link
      // cannot diverge from the navigation. The shared screens (dashboard/
      // transactions) admit every authenticated session.
      ...APP_ROUTES.map((route) => ({
        path: route.path,
        element: <RequireRoute routeKey={route.path}>{route.element}</RequireRoute>,
      })),
      // Deep-linked screens that are not part of the navigation (permission is
      // enforced in-page — e.g. the CES capture page's canEditTransaction guard).
      ...EXTRA_ROUTES.map((route) => ({ path: route.path, element: route.element })),
      // Catch-all: any unknown path shows the branded Not-Found screen.
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);

export function App(): React.JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          {/* Inside AuthProvider (it mirrors the choice onto the signed-in user's
              row) and OUTSIDE AuthGate, so the login screen is themed too. */}
          <UiPrefsProvider>
            <AuthGate>
              <RouterProvider router={router} />
            </AuthGate>
          </UiPrefsProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
